import React, { useState } from "react";
import styles from "./styles.module.scss";
import responsiveStyles from "./responsiveStyles.module.scss";
import { Wrapper } from "../../reusable";
import { Data8Service } from "../../../services/data-8";

function NewsletterSignup() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState("");

  const validateWithData8 = async (value: string) => {
    if (value && value.trim().length > 0) {
      try {
        const data8Valid = await Data8Service("EmailValidation", "IsValid", {
          email: value,
        });
        if (data8Valid.Result === "Invalid") {
          setError("Invalid email");
          return false;
        }
      } catch (e) {
        // If Data8 fails, keep UX permissive but do not throw
      }
    }
    setError("");
    return true;
  };

  const onEmailChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    setEmail(value);
    setSubmitted(false);

    if (!value) {
      setError("");
      return;
    }

    setIsValidating(true);
    await validateWithData8(value);
    setIsValidating(false);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || error) return;

    setIsLoading(true);

    // Ensure latest value is valid before posting
    const ok = await validateWithData8(email);
    if (!ok) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_UPLOAD_EMAIL}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email }),
        }
      );

      if (response.ok) {
        setSubmitted(true);
        setEmail("");
        setError("");
      } else {
        setError("Failed to subscribe. Please try again.");
      }
    } catch (err) {
      setError("Network error. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section className={`${styles.section} ${responsiveStyles.section}`}>
      <Wrapper>
        <div className={`${styles.inner} ${responsiveStyles.inner}`}>
          <div className={styles.header}>
            <h2>Get updates</h2>
            <p>Sign up to our newsletter to receive updates and insights.</p>
          </div>
          <div className={styles.card}>
            <form className={styles.form} onSubmit={onSubmit}>
              <input
                className={`${styles.emailInput} ${
                  error ? styles.emailInputError : ""
                }`}
                type="email"
                name="email"
                aria-label="Email address"
                placeholder="Enter your email"
                required
                value={email}
                onChange={onEmailChange}
                disabled={isLoading}
              />
              <button
                type="submit"
                className={`${styles.submitBtn} ${
                  email ? styles.submitBtnActive : styles.submitBtnIdle
                }`}
                disabled={
                  submitted || isLoading || !email || !!error || isValidating
                }
                aria-label="Subscribe to newsletter"
              >
                {submitted
                  ? "Thanks!"
                  : isLoading
                  ? "Subscribing..."
                  : isValidating
                  ? "Checking..."
                  : "Subscribe"}
              </button>
            </form>
            <p
              className={styles.errorText}
              style={{
                opacity: error ? 1 : 0,
                pointerEvents: error ? "auto" : "none",
              }}
            >
              {error || ""}
            </p>
            <p
              className={styles.helperText}
              style={{
                opacity: !submitted && !error ? 1 : 0,
                pointerEvents: !submitted && !error ? "auto" : "none",
              }}
            >
              No spam. Unsubscribe any time.
            </p>
          </div>
        </div>
      </Wrapper>
    </section>
  );
}

export default NewsletterSignup;
